<template>
  <div class="invoice-history-component">
    <p>
      {{ $t("dashboard.sales_transactions.description") }}
      <a
        href="https://www.soldera.org/article/mis-on-paritolutunnistuste-hind-siin-on-moned-avalikud-hinnasignaalid"
        target="_blank"
      >
        {{ $t("dashboard.sales_transactions.description_link") }}</a
      >
    </p>
    <LoadingIndicatorComponent v-if="isLoadingTransactions" />
    <PlaceholderCardComponent
      v-else-if="salesTransactions.length === 0"
      :title="$t('dashboard.sales_transactions.no_sales_transactions')"
      :subtitle="$t('dashboard.sales_transactions.notificationInfo')"
    />
    <template v-else>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div
          class="ds-segctrl"
          role="group"
          :aria-label="$t('dashboard.sales_transactions.title')"
        >
          <button
            type="button"
            :aria-pressed="viewMode === 'aggregated'"
            :class="{ 'is-on': viewMode === 'aggregated' }"
            @click="selectViewMode('aggregated')"
          >
            {{ $t("dashboard.sales_transactions.view_aggregated") }}
          </button>
          <button
            v-if="showCompanyColumn"
            type="button"
            :aria-pressed="viewMode === 'breakdown'"
            :class="{ 'is-on': viewMode === 'breakdown' }"
            @click="selectViewMode('breakdown')"
          >
            {{ $t("dashboard.sales_transactions.view_by_company") }}
          </button>
          <button
            type="button"
            :aria-pressed="viewMode === 'device'"
            :class="{ 'is-on': viewMode === 'device' }"
            @click="selectViewMode('device')"
          >
            {{ $t("dashboard.sales_transactions.view_by_power_plant") }}
          </button>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="isExporting"
          @click="exportTransactions"
        >
          <FontAwesomeIcon
            v-if="isExporting"
            icon="spinner"
            spin
            class="me-1"
          />
          <FontAwesomeIcon v-else icon="download" class="me-1" />
          {{ $t("dashboard.sales_transactions.export_xlsx") }}
        </button>
      </div>
      <LoadingIndicatorComponent
        v-if="isLoadingDevices && viewMode === 'device'"
      />
      <DataTable
        v-else
        :value="displayRows"
        v-model:filters="filters"
        dataKey="key"
        filterDisplay="row"
        class="border rounded"
        :scrollable="displayRows.length > 14"
        :scrollHeight="displayRows.length > 14 ? '600px' : undefined"
        :virtualScrollerOptions="
          displayRows.length > 14
            ? { itemSize: 42, numToleratedItems: 20, autoSize: true }
            : undefined
        "
      >
        <Column
          :header="$t('dashboard.sales_transactions.date')"
          sortField="transaction.transaction_at"
          filterField="transaction.transaction_at"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 6rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueDates"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <PendingCell :pending="data.pending">
              <span v-if="!nda_signed_link" class="fw-bold">
                {{ formatDateTimeIntoDate(data.transaction.transaction_at) }}
              </span>
              <a
                v-else-if="data.transaction.transaction_link"
                :href="data.transaction.transaction_link"
                target="_blank"
                class="fw-bold"
              >
                {{
                  formatDateTimeIntoDate(data.transaction.transaction_at || "")
                }}
              </a>
              <span v-else class="fw-bold">
                {{
                  data.transaction.transaction_at
                    ? formatDateTimeIntoDate(data.transaction.transaction_at)
                    : "N/A"
                }}
              </span>
            </PendingCell>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.item')"
          sortField="transaction.description"
          filterField="transaction.description"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 9rem"
          class="wrap-text"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              class="p-column-filter w-100"
              :placeholder="$t('common.filter')"
              @input="filterCallback()"
            />
          </template>
          <template #body="{ data }">
            <span class="fw-bold">{{ data.transaction.description }}</span>
          </template>
        </Column>
        <Column
          v-if="viewMode === 'device'"
          :header="$t('dashboard.sales_transactions.power_plant')"
          sortField="deviceName"
          filterField="deviceName"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 10rem"
          class="wrap-text"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueDeviceNames"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            {{ data.deviceName ?? "—" }}
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.final_unit_price')"
          sortField="priceSort"
          filterField="priceDisplay"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 7rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniquePrices"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <div class="tabular text-end">
              <PendingCell :pending="data.pending">
                {{ data.priceDisplay }} €
              </PendingCell>
            </div>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.offer_range')"
          sortField="minPriceSort"
          filterField="offerRange"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 7rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueOfferRanges"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <div class="tabular text-end">
              <PendingCell :pending="data.pending">
                {{ data.offerRange }} €
              </PendingCell>
            </div>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.amount')"
          sortField="amount"
          sortable
          headerStyle="width: 5rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <div class="tabular text-end">{{ data.amount }} MWh</div>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.revenue')"
          sortField="revenueSort"
          filterField="revenue"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 7rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueRevenues"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <div class="tabular text-end">
              <PendingCell :pending="data.pending">
                {{ moneyText(data.revenue) }}
              </PendingCell>
            </div>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.soldera_fees')"
          sortField="solderaFeesSort"
          filterField="solderaFees"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 7rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueSolderaFees"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <div class="tabular text-end">
              <PendingCell :pending="data.pending">
                {{ moneyText(data.solderaFees) }}
              </PendingCell>
            </div>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.registry_fees')"
          sortField="registryFeesSort"
          filterField="registryFees"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 7rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueRegistryFees"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <div class="tabular text-end">
              <PendingCell :pending="data.pending">
                {{ moneyText(data.registryFees) }}
              </PendingCell>
            </div>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.fees_pending')"
          sortField="feesPending"
          filterField="feesPending"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 5rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="feesPendingOptions"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            {{ data.feesPending ? $t("common.yes") : $t("common.no") }}
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.final_country')"
          sortField="transaction.counterparty_country"
          filterField="transaction.counterparty_country"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 5rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueCountries"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <PendingCell :pending="data.pending">
              {{ data.transaction.counterparty_country }}
            </PendingCell>
          </template>
        </Column>
        <Column
          :header="$t('dashboard.sales_transactions.status')"
          sortField="transaction.status"
          filterField="transaction.status"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 6rem"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="statusOptions"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            <span
              :class="[
                'badge',
                data.transaction.status === 'COMPLETED'
                  ? 'bg-success'
                  : 'bg-warning text-dark',
              ]"
              :title="
                data.transaction.status !== 'COMPLETED'
                  ? $t('dashboard.sales_transactions.processing_explanation')
                  : ''
              "
            >
              {{
                data.transaction.status === "COMPLETED"
                  ? $t("dashboard.sales_transactions.completed")
                  : $t("dashboard.sales_transactions.processing")
              }}
            </span>
          </template>
        </Column>
        <Column
          v-if="showCompanyColumn"
          :header="$t('common.company')"
          sortField="companyNames"
          filterField="companyNameList"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          headerStyle="width: 14rem"
          class="wrap-text"
        >
          <template #sorticon="{ sortOrder: so }">
            <FontAwesomeIcon
              :icon="getSortIcon(so)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect
              v-model="filterModel.value"
              :options="uniqueCompanyNames"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('common.filter')"
              class="p-column-filter w-100"
              filter
              @change="filterCallback()"
              @selectall-change="filterCallback()"
            >
              <template #filtericon>
                <FontAwesomeIcon
                  icon="search"
                  class="text-muted pv-multiselect-filter-icon"
                />
              </template>
              <template #value="{ value, placeholder }">
                <span v-if="value && value.length" class="text-primary fw-bold">
                  {{ $t("dashboard.portfolio.table.filters.active") }}
                </span>
                <span v-else class="text-muted">{{ placeholder }}</span>
              </template>
            </MultiSelect>
          </template>
          <template #body="{ data }">
            {{ data.companyNames }}
          </template>
        </Column>
      </DataTable>
    </template>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import {
  formatDateTimeIntoDate,
  formatMoneyString,
} from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import PendingCell from "@/money/PendingCell.vue";
import {
  Company,
  MultiCompanySalesTransaction,
  SalesTransactionItemDeviceVolume,
} from "@/types";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { FilterMatchMode, FilterService } from "@primevue/core/api";
import { groupBy } from "lodash-es";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getSortIcon } from "@/utils/sortIcons";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// NOTE: match on the formatted calendar day so transactions with different
// timestamps on the same day group under one filter option.
FilterService.register(
  "dateIn",
  (value: string | null, filterValues: string[]): boolean => {
    if (!filterValues || !filterValues.length) return true;
    if (!value) return false;
    return filterValues.includes(formatDateTimeIntoDate(value));
  }
);

// NOTE: the column displays every non-COMPLETED status as "processing", so the
// filter matches on that same two-way split rather than the raw status values.
FilterService.register(
  "statusKindIn",
  (value: string | null, filterValues: string[]): boolean => {
    if (!filterValues || !filterValues.length) return true;
    return filterValues.includes(
      value === "COMPLETED" ? "COMPLETED" : "PROCESSING"
    );
  }
);

// NOTE: aggregated rows span several companies; match when any selected
// company participates in the row.
FilterService.register(
  "companyAnyIn",
  (value: string[] | null, filterValues: string[]): boolean => {
    if (!filterValues || !filterValues.length) return true;
    if (!value) return false;
    return filterValues.some((name) => value.includes(name));
  }
);

const props = defineProps<{
  companyIds: number[];
  companies?: Company[];
  nda_signed_link?: string;
}>();

type ViewMode = "aggregated" | "breakdown" | "device";

interface TransactionRow {
  key: string;
  transaction: MultiCompanySalesTransaction;
  amount: number;
  deviceName: string | null;
  priceDisplay: string | null;
  offerRange: string | null;
  pending: boolean;
  companyNames: string;
  companyNameList: string[];
  revenue: string | null;
  solderaFees: string | null;
  registryFees: string | null;
  feesPending: boolean;
  priceSort: number | null;
  minPriceSort: number | null;
  revenueSort: number | null;
  solderaFeesSort: number | null;
  registryFeesSort: number | null;
}

const salesTransactions = ref<MultiCompanySalesTransaction[]>([]);
const deviceVolumes = ref<SalesTransactionItemDeviceVolume[] | null>(null);
const viewMode = ref<ViewMode>("aggregated");
const isLoadingTransactions = ref(false);
const isLoadingDevices = ref(false);
const isExporting = ref(false);
const { globalErrorHandler } = useGlobalHandlers();
const { t } = useI18n();
let fetchRequestId = 0;
let deviceFetchRequestId = 0;

const filters = ref({
  "transaction.transaction_at": { value: [], matchMode: "dateIn" as any },
  "transaction.description": {
    value: null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  deviceName: { value: [], matchMode: FilterMatchMode.IN },
  priceDisplay: { value: [], matchMode: FilterMatchMode.IN },
  offerRange: { value: [], matchMode: FilterMatchMode.IN },
  revenue: { value: [], matchMode: FilterMatchMode.IN },
  solderaFees: { value: [], matchMode: FilterMatchMode.IN },
  registryFees: { value: [], matchMode: FilterMatchMode.IN },
  feesPending: { value: [], matchMode: FilterMatchMode.IN },
  "transaction.counterparty_country": {
    value: [],
    matchMode: FilterMatchMode.IN,
  },
  "transaction.status": { value: [], matchMode: "statusKindIn" as any },
  companyNameList: { value: [], matchMode: "companyAnyIn" as any },
});

const showCompanyColumn = computed(() => (props.companies?.length ?? 0) > 1);

const isPendingPrice = (price: string | null): boolean => {
  if (!price) return true;
  return parseFloat(price) === 0;
};

const moneyText = (value: string | null): string =>
  value === null ? "— €" : `${formatMoneyString(value)} €`;

const offerRangeText = (
  minPrice: string | null,
  rangeTop: string | null
): string | null => (minPrice === null ? null : `${minPrice} - ${rangeTop}`);

const toSortNumber = (value: string | null): number | null => {
  if (value === null) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const aggregatedPriceDisplay = (
  transaction: MultiCompanySalesTransaction
): string | null => {
  if (transaction.price_min === null) return null;
  if (
    transaction.price_max !== null &&
    parseFloat(transaction.price_max) !== parseFloat(transaction.price_min)
  ) {
    return `${transaction.price_min} – ${transaction.price_max}`;
  }
  return transaction.price_min;
};

const devicesByTransactionItem = computed(() =>
  groupBy(deviceVolumes.value ?? [], "sales_transaction_item_id")
);

const displayRows = computed<TransactionRow[]>(() => {
  if (viewMode.value === "device") {
    return salesTransactions.value.flatMap((transaction) =>
      transaction.items.flatMap((item) =>
        (
          devicesByTransactionItem.value[item.sales_transaction_item_id] ?? []
        ).map((device, index) => ({
          key: `${device.sales_transaction_item_id}-${index}`,
          transaction,
          amount: device.amount,
          deviceName: device.production_device_name || null,
          priceDisplay: item.effective_unit_price,
          offerRange: offerRangeText(
            transaction.min_price,
            item.effective_unit_price
          ),
          pending: isPendingPrice(item.effective_unit_price),
          companyNames: item.company_name,
          companyNameList: [item.company_name],
          revenue: device.revenue,
          solderaFees: device.soldera_fees,
          registryFees: device.registry_fees,
          feesPending: item.fees_pending,
          priceSort: toSortNumber(item.effective_unit_price),
          minPriceSort: toSortNumber(transaction.min_price),
          revenueSort: toSortNumber(device.revenue),
          solderaFeesSort: toSortNumber(device.soldera_fees),
          registryFeesSort: toSortNumber(device.registry_fees),
        }))
      )
    );
  }
  if (viewMode.value === "breakdown" && showCompanyColumn.value) {
    return salesTransactions.value.flatMap((transaction) =>
      transaction.items.map((item) => ({
        key: `${item.sales_transaction_item_id}`,
        transaction,
        amount: item.amount,
        deviceName: null,
        priceDisplay: item.effective_unit_price,
        offerRange: offerRangeText(
          transaction.min_price,
          item.effective_unit_price
        ),
        pending: isPendingPrice(item.effective_unit_price),
        companyNames: item.company_name,
        companyNameList: [item.company_name],
        revenue: item.revenue,
        solderaFees: item.soldera_fees,
        registryFees: item.registry_fees,
        feesPending: item.fees_pending,
        priceSort: toSortNumber(item.effective_unit_price),
        minPriceSort: toSortNumber(transaction.min_price),
        revenueSort: toSortNumber(item.revenue),
        solderaFeesSort: toSortNumber(item.soldera_fees),
        registryFeesSort: toSortNumber(item.registry_fees),
      }))
    );
  }
  return salesTransactions.value.map((transaction) => ({
    key: `${transaction.id}`,
    transaction,
    amount: transaction.total_amount,
    deviceName: null,
    priceDisplay: aggregatedPriceDisplay(transaction),
    offerRange: offerRangeText(transaction.min_price, transaction.price_max),
    pending: isPendingPrice(transaction.price_min),
    companyNames: transaction.items.map((item) => item.company_name).join(", "),
    companyNameList: transaction.items.map((item) => item.company_name),
    revenue: transaction.total_revenue,
    solderaFees: transaction.total_soldera_fees,
    registryFees: transaction.total_registry_fees,
    feesPending: transaction.fees_pending,
    priceSort: toSortNumber(transaction.price_min),
    minPriceSort: toSortNumber(transaction.min_price),
    revenueSort: toSortNumber(transaction.total_revenue),
    solderaFeesSort: toSortNumber(transaction.total_soldera_fees),
    registryFeesSort: toSortNumber(transaction.total_registry_fees),
  }));
});

const uniqueDates = computed(() => {
  const dateMap = new Map<string, string>();
  for (const transaction of salesTransactions.value) {
    if (!transaction.transaction_at) continue;
    const formatted = formatDateTimeIntoDate(transaction.transaction_at);
    if (!dateMap.has(formatted)) {
      dateMap.set(formatted, transaction.transaction_at);
    }
  }
  return [...dateMap.entries()]
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([label]) => ({ label, value: label }));
});

const uniqueDeviceNames = computed(() =>
  [
    ...new Set(
      (deviceVolumes.value ?? [])
        .map((device) => device.production_device_name)
        .filter(Boolean)
    ),
  ]
    .sort((a, b) => a.localeCompare(b, undefined, { ignorePunctuation: true }))
    .map((name) => ({ label: name, value: name }))
);

const uniqueCountries = computed(() =>
  [
    ...new Set(
      salesTransactions.value
        .map((transaction) => transaction.counterparty_country)
        .filter((country): country is string => Boolean(country))
    ),
  ]
    .sort()
    .map((country) => ({ label: country, value: country }))
);

const uniqueCompanyNames = computed(() =>
  [
    ...new Set(
      salesTransactions.value.flatMap((transaction) =>
        transaction.items.map((item) => item.company_name)
      )
    ),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { ignorePunctuation: true }))
    .map((name) => ({ label: name, value: name }))
);

const toFilterOptions = (
  values: (string | null)[],
  label: (value: string) => string
) =>
  [...new Set(values.filter((value): value is string => value !== null))]
    .sort((a, b) => parseFloat(a) - parseFloat(b))
    .map((value) => ({ label: label(value), value }));

const uniquePrices = computed(() =>
  toFilterOptions(
    displayRows.value.map((row) => row.priceDisplay),
    (value) => `${value} €`
  )
);

const uniqueOfferRanges = computed(() =>
  toFilterOptions(
    displayRows.value.map((row) => row.offerRange),
    (value) => `${value} €`
  )
);

const uniqueRevenues = computed(() =>
  toFilterOptions(
    displayRows.value.map((row) => row.revenue),
    moneyText
  )
);

const uniqueSolderaFees = computed(() =>
  toFilterOptions(
    displayRows.value.map((row) => row.solderaFees),
    moneyText
  )
);

const uniqueRegistryFees = computed(() =>
  toFilterOptions(
    displayRows.value.map((row) => row.registryFees),
    moneyText
  )
);

const feesPendingOptions = computed(() => [
  { label: t("common.yes"), value: true },
  { label: t("common.no"), value: false },
]);

const statusOptions = computed(() => [
  { label: t("dashboard.sales_transactions.completed"), value: "COMPLETED" },
  { label: t("dashboard.sales_transactions.processing"), value: "PROCESSING" },
]);

function selectViewMode(mode: ViewMode): void {
  viewMode.value = mode;
  if (mode === "device" && deviceVolumes.value === null) {
    void getDeviceVolumes();
  }
}

async function getSalesTransactions(): Promise<void> {
  // NOTE: an empty company_ids array is dropped from the query string, which the
  // backend treats as "all owned companies" — an empty selection must show nothing.
  if (props.companyIds.length === 0) {
    fetchRequestId += 1;
    salesTransactions.value = [];
    isLoadingTransactions.value = false;
    return;
  }
  const currentRequestId = ++fetchRequestId;
  isLoadingTransactions.value = true;
  try {
    const response = await goSolidApi.get("/api/companies/sales-transactions", {
      params: { company_ids: props.companyIds },
      paramsSerializer: { indexes: null },
    });
    if (currentRequestId !== fetchRequestId) return;
    salesTransactions.value = response.data.sales_transactions;
  } catch (error) {
    if (currentRequestId !== fetchRequestId) return;
    globalErrorHandler(error);
    salesTransactions.value = [];
  } finally {
    if (currentRequestId === fetchRequestId) {
      isLoadingTransactions.value = false;
    }
  }
}

async function getDeviceVolumes(): Promise<void> {
  if (props.companyIds.length === 0) {
    deviceFetchRequestId += 1;
    deviceVolumes.value = [];
    isLoadingDevices.value = false;
    return;
  }
  const currentRequestId = ++deviceFetchRequestId;
  isLoadingDevices.value = true;
  try {
    const response = await goSolidApi.get(
      "/api/companies/sales-transactions/devices",
      {
        params: { company_ids: props.companyIds },
        paramsSerializer: { indexes: null },
      }
    );
    if (currentRequestId !== deviceFetchRequestId) return;
    deviceVolumes.value = response.data.devices;
  } catch (error) {
    if (currentRequestId !== deviceFetchRequestId) return;
    globalErrorHandler(error);
    // NOTE: null (not []) so re-entering the power-plant view retries the fetch.
    deviceVolumes.value = null;
  } finally {
    if (currentRequestId === deviceFetchRequestId) {
      isLoadingDevices.value = false;
    }
  }
}

async function exportTransactions(): Promise<void> {
  if (isExporting.value || props.companyIds.length === 0) return;
  isExporting.value = true;
  try {
    const response = await goSolidApi.get(
      "/api/companies/sales-transactions/export",
      {
        params: { company_ids: props.companyIds },
        paramsSerializer: { indexes: null },
        responseType: "blob",
      }
    );
    downloadFromApiResponse(
      response.data,
      response.headers,
      "soldera_transactions_export.xlsx"
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isExporting.value = false;
  }
}

async function reloadTransactions(): Promise<void> {
  deviceVolumes.value = null;
  await getSalesTransactions();
  if (viewMode.value === "device") {
    await getDeviceVolumes();
  }
}

watch(() => props.companyIds, reloadTransactions, { immediate: true });
</script>

<style scoped lang="scss">
.tabular {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
</style>
